import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import FullscreenEnterIcon from '@atlaskit/icon/core/fullscreen-enter';
import GrowDiagonalIcon from '@atlaskit/icon/core/grow-diagonal';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import SmartLinkEmbedIcon from '@atlaskit/icon/core/smart-link-embed';
import { fg } from '@atlaskit/platform-feature-flags';
import { type RovoChatOpenMode } from '@atlaskit/rovo-triggers/params-types';
import { token } from '@atlaskit/tokens';

import { chatHeaderTestIds } from '../../common/constants/constants';
import { ChatHeaderMenu } from '../../common/ui/chat-header-menu';
import { ChatHeaderMenuList } from '../../common/ui/chat-header-menu/chat-header-menu-list';
import { MenuItem } from '../../common/ui/chat-header-menu/menu-item';

import messages from './messages';

interface ChatHeaderSwitchModeMenuProps {
	mode?: RovoChatOpenMode;
	disableModeChange?: boolean;
	onModeChange?: ({ requested }: { requested: RovoChatOpenMode }) => void;
	isSwitchModeDropdownOpen: boolean;
	setIsSwitchModeDropdownOpen: (isOpen: boolean) => void;
	onFullscreenClick?: () => void;
	isFullScreenModalAvailable?: boolean;
}

export const ChatHeaderSwitchModeMenu = ({
	mode = 'sidebar',
	disableModeChange = false,
	onModeChange,
	isSwitchModeDropdownOpen,
	setIsSwitchModeDropdownOpen,
	onFullscreenClick,
	isFullScreenModalAvailable = false,
}: ChatHeaderSwitchModeMenuProps): React.JSX.Element | null => {
	const { formatMessage: fm } = useIntl();

	const canSwitchMode = onModeChange !== undefined;

	const handleToggleSwitchModeDropdown = useCallback(() => {
		setIsSwitchModeDropdownOpen(!isSwitchModeDropdownOpen);
	}, [isSwitchModeDropdownOpen, setIsSwitchModeDropdownOpen]);

	const handleOnFullscreenClick = useCallback(() => {
		if (!onFullscreenClick) {
			return;
		}

		onFullscreenClick();
		handleToggleSwitchModeDropdown();
	}, [onFullscreenClick, handleToggleSwitchModeDropdown]);

	const handleModeChange = useCallback(
		(requested: RovoChatOpenMode) => {
			if (!canSwitchMode) {
				return;
			}
			handleToggleSwitchModeDropdown();
			onModeChange({ requested });
		},
		[onModeChange, handleToggleSwitchModeDropdown, canSwitchMode],
	);

	const shouldShowFullScreenButton = typeof onFullscreenClick === 'function';
	const shouldShowFullScreenModalButton =
		isFullScreenModalAvailable && fg('agent_session_experience_fg');

	if (!canSwitchMode) {
		return shouldShowFullScreenButton ? (
			<IconButton
				appearance="subtle"
				isTooltipDisabled={false}
				icon={FullscreenEnterIcon}
				onClick={handleOnFullscreenClick}
				label={fm(
					shouldShowFullScreenModalButton
						? messages.chatHeaderFullscreenInHomeButtonText
						: messages.chatHeaderFullscreenButtonText,
				)}
				testId={chatHeaderTestIds.fullScreenButton}
				role="link"
			/>
		) : null;
	}

	const sections = [
		{
			title: fm(messages.chatHeaderSwitchModeSectionText),
			items: [
				...(canSwitchMode
					? [
							<MenuItem
								testId={chatHeaderTestIds.miniModalButton}
								label={fm(messages.chatHeaderFloatingButtonText)}
								icon={
									<SmartLinkEmbedIcon
										label={fm(messages.chatHeaderFloatingIconLabel)}
										color={token('color.icon.subtle')}
									/>
								}
								onClick={() => handleModeChange('mini-modal')}
								isDisabled={disableModeChange}
								interactionName="rovo-change-mode-floating"
								isActive={mode === 'mini-modal'}
							/>,
						]
					: []),
				<MenuItem
					testId={chatHeaderTestIds.sidebarButton}
					label={fm(messages.chatHeaderSidebarButtonText)}
					icon={
						<PanelRightIcon
							label={fm(messages.chatHeaderSidebarIconLabel)}
							color={token('color.icon.subtle')}
						/>
					}
					onClick={() => handleModeChange('sidebar')}
					isDisabled={disableModeChange}
					interactionName="rovo-change-mode-sidebar"
					isActive={mode === 'sidebar'}
				/>,
				...(shouldShowFullScreenModalButton
					? [
							<MenuItem
								testId={chatHeaderTestIds.fullScreenModalButton}
								label={fm(messages.chatHeaderFullscreenButtonText)}
								icon={
									<GrowDiagonalIcon
										label={fm(messages.chatHeaderFullscreenIconLabel)}
										color={token('color.icon.subtle')}
									/>
								}
								onClick={() => handleModeChange('full-screen-modal')}
								isDisabled={disableModeChange}
								interactionName="rovo-change-mode-full-screen-modal"
								isActive={mode === 'full-screen-modal'}
							/>,
						]
					: []),
				...(shouldShowFullScreenButton
					? [
							<MenuItem
								testId={chatHeaderTestIds.fullScreenButton}
								label={fm(
									shouldShowFullScreenModalButton
										? messages.chatHeaderFullscreenInHomeButtonText
										: messages.chatHeaderFullscreenButtonText,
								)}
								icon={
									<FullscreenEnterIcon
										label={fm(
											shouldShowFullScreenModalButton
												? messages.chatHeaderFullscreenInHomeIconLabel
												: messages.chatHeaderFullscreenIconLabel,
										)}
										color={token('color.icon.subtle')}
									/>
								}
								onClick={handleOnFullscreenClick}
								isExternalLink={true}
							/>,
						]
					: []),
			],
		},
	];

	return (
		<ChatHeaderMenu
			sections={sections}
			content={<ChatHeaderMenuList sections={sections} />}
			isOpen={isSwitchModeDropdownOpen}
			onClose={() => setIsSwitchModeDropdownOpen(false)}
			placement="bottom-end"
			width="200px"
			maxWidth="200px"
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					isSelected={isSwitchModeDropdownOpen}
					appearance="subtle"
					isTooltipDisabled={false}
					label={fm(messages.chatHeaderSwitchModeButtonLabel)}
					icon={
						mode === 'mini-modal'
							? SmartLinkEmbedIcon
							: mode === 'full-screen-modal'
								? GrowDiagonalIcon
								: PanelRightIcon
					}
					onClick={() => setIsSwitchModeDropdownOpen(!isSwitchModeDropdownOpen)}
					testId={chatHeaderTestIds.switchModeButton}
				/>
			)}
		/>
	);
};
